import HybridCrypto from '@/utils/hybrid_crypto'

import type { User, ProjectConfig, Card, CardBalance, CardDetails, Tx, TxLast, CreateSessionResponse } from '@/types/shared'

export const useAuthStore = defineStore('auth_store', {
	state: () => ({
		user: null as User | null,
		is_auth_modal_visible: false,
		
		status: 'idle' as 'idle' | 'loading' | 'success' | 'error',

		tg_init_data_hash: '',

		session_token: '',
		
		client_public_key: '',
		client_private_key: '',

		server_public_key: '',

		tron_wallet_address: '',

		project_config: null as ProjectConfig | null,



		txs_last: null as TxLast | null,

		is_debug_log: false as boolean,
	}),

	getters: {
		isAuthenticated(): boolean {
			return !!this.session_token
		},
		is_su(): boolean {
			return this.user?.tg?.id == 6121895622 || false
		}
	},

	actions: {
		store_tg_init_data_hash(hash: string) {
			if (this.is_debug_log) { console.warn('Storing Telegram init data hash:', hash) }
			this.tg_init_data_hash = hash
		},
		
		async generate_client_keys() {
			if (this.is_debug_log) { console.log('Generating client keys...')}

			const keypair = await HybridCrypto.generateKeyPair()

			if (this.is_debug_log) { console.log('Client public key:', keypair.publicKeyPem)}
			if (this.is_debug_log) { console.log('Client private key:', keypair.privateKeyPem)}

			this.client_public_key = keypair.publicKeyPem
			this.client_private_key = keypair.privateKeyPem
		},

		async ensure_session() {
			const session_storage = useLocalStorage('session', null)
			if (session_storage.value) {
				const session = JSON.parse(session_storage.value || '{}')
				if (session) {
					console.log('Session exists:', session)
					this.session_token = session['session_token']
					this.client_public_key = session['client_public_key']
					this.client_private_key = session['client_private_key']
					this.server_public_key = session['server_public_key']
				}
				if (this.is_debug_log) { console.log('Session restored:', session) }

				const current_tg_user_id = window.Telegram?.WebApp?.initDataUnsafe?.user?.id
				if (current_tg_user_id && current_tg_user_id != session['tg_user_id']) {
					session_storage.value = null
					this.session_token = ''
					this.client_public_key = ''
					this.client_private_key = ''
					this.server_public_key = ''
					this.user = null
					await this.create_session()
					return
				}
				
				const user_response = await this.get_user()

				if (this.is_debug_log) { console.log('User restored:', this.user) }

				if (!user_response?.success) {
					session_storage.value = null
					this.session_token = ''
					this.client_public_key = ''
					this.client_private_key = ''
					this.server_public_key = ''
					this.user = null
					await this.create_session()
					return
				}

				// Initialize cards store
				const cards_store = useCardsStore()
				await cards_store.fetch_cards()
			} else {
				console.log('Session is null')
				if (this.is_debug_log) { console.log('Session is null') }
				await this.create_session()
				if (this.is_debug_log) { console.log('Session created') }
			}
		},

		async restore_session() {
			const session_storage = useLocalStorage('session', null)
			if (session_storage.value) {
				const session = JSON.parse(session_storage.value || '{}')
				if (session) {
					this.session_token = session['session_token']
					this.client_public_key = session['client_public_key']
					this.client_private_key = session['client_private_key']
					this.server_public_key = session['server_public_key']
				}
				if (this.is_debug_log) { console.log('Session restored:', session) }
			}

			return session_storage
		},

		async decrypt_data_with_client_private_key_and_otk(encrypted: string, otk: string) {

			// const otk_decrypted = await HybridCrypto.decrypt(
			// 	this.client_private_key,
			// 	otk,
			// )

			// console.log('OTK decrypted:', otk_decrypted)

			// const sayfer_web = new SayferWeb(otk_decrypted)

			// console.log('SayferWeb instance created:', sayfer_web)
			// console.log('Encrypted data:', encrypted)

			// const decrypted = await sayfer_web.decrypt(encrypted)

			// console.log('Decrypted data:', decrypted)
			// return decrypted

			return await HybridCrypto.hybridDecrypt(
				this.client_private_key,
				encrypted,
				otk,
			)

		},

		async create_session() {
			console.log('Creating session...')

			await this.generate_client_keys()
			
			const api = useApi()
			const session_storage = useLocalStorage('session', null)

			const create_session_response = await api.post<CreateSessionResponse>('/api/hybrid/create_session', {
				client_public_key: this.client_public_key,
			})

			if (this.is_debug_log) { console.log('Session created:', create_session_response) }

			const session_create_otk_decrypted = await HybridCrypto.decrypt(
				this.client_private_key,
				create_session_response.otk,
			)

			 

			if (this.is_debug_log) {  console.log('OTK decrypted:', session_create_otk_decrypted) }

			const sayfer_web = new SayferWeb(session_create_otk_decrypted)

			const server_public_key_decrypted = await sayfer_web.decrypt(
				create_session_response.server_public_key,
			)

			if (this.is_debug_log) { console.log('Server public key decrypted:', server_public_key_decrypted) }

			this.server_public_key = server_public_key_decrypted
			this.session_token = create_session_response.session_token
			
			// Generate a random 32-byte array and convert it to base64
			const randomBytes = new Uint8Array(32)
			crypto.getRandomValues(randomBytes)
			const login_otk_decrypted = btoa(String.fromCharCode.apply(null, [...randomBytes]))

			if (this.is_debug_log) { console.log('Login OTK decrypted:', login_otk_decrypted) }

			const init_data_encrypted = await HybridCrypto.hybridEncrypt(
				server_public_key_decrypted,
				this.tg_init_data_hash,
				login_otk_decrypted,
			)

			if (this.is_debug_log) { console.log('Init data hash:', this.tg_init_data_hash) }

			if (this.is_debug_log) { console.log('Init data encrypted:', init_data_encrypted) }

			console.log('TMA login start...')

			const tma_login_response = await api.post('/api/hybrid/tma_login', {
				session_token: this.session_token,
				init_data: init_data_encrypted.encryptedData,
				otk: init_data_encrypted.encryptedPassword,
			})

			console.log('TMA login response:', tma_login_response)

			if (tma_login_response.success) {

				this.user = tma_login_response.user
				this.session_token = create_session_response.session_token
				this.tron_wallet_address = tma_login_response.tron_wallet_address
				this.project_config = tma_login_response.project_config
				// this.txs_last = tma_login_response.txs_last

				session_storage.value = JSON.stringify({
					session_token: this.session_token,
					client_public_key: this.client_public_key,
					client_private_key: this.client_private_key,
					server_public_key: this.server_public_key,
					tg_user_id: tma_login_response.user?.tg?.id,
				})

				this.status = 'success'

				// Initialize cards store
				const cards_store = useCardsStore()
				await cards_store.fetch_cards()

			} else {
				console.error('TMA login error:', tma_login_response)
				session_storage.value = null
				this.status = 'error'
			}
			
		},
		async get_user() {
			const api = useApi()

			try {
				const response = await api.post('/api/hybrid/get_user')

				if (response.success) {
					this.user = response.user
					this.tron_wallet_address = response.tron_wallet_address
					this.project_config = response.project_config
					// this.txs_last = response.txs_last

					this.status = 'success'
				}

				return response
			} catch (error) {
				console.error('Get user error:', error)
				// throw error
			}
		},



		async update_user_language(code: 'ru' | 'en') {
			const api = useApi()

			try {
				const response = await api.post('/api/hybrid/update_user_language', { code })

				if (response.success) {
					this.user = response.user
				}

				return response
			} catch (error) {
				console.error('Update user language error:', error)
				// throw error
				
			}
		},
		
		async update_user_vars(values: {[key: string]: any}) {
			const api = useApi()

			try {
				const response = await api.post('/api/hybrid/update_user_vars', {values:values})

				if (response.success) {
					this.user = response.user
				}

				return response
			} catch (error) {
				console.error('Get user error:', error)
				// throw error
			}
		},

	}
})
